import React, { useState, useEffect } from 'react';
import { QrCode, Download, Copy, Check, Printer } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ProductQRCode = ({ productId, productName, price }) => {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    if (showQR && !qrData) {
      fetchQRCode();
    }
  }, [showQR, productId]);

  const fetchQRCode = async () => {
    try {
      const response = await fetch(`${API}/api/products/${productId}/qr`);
      if (response.ok) {
        const data = await response.json();
        setQrData(data);
      }
    } catch (error) {
      console.error('Error fetching QR:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopySKU = () => {
    if (qrData?.sku) {
      navigator.clipboard.writeText(qrData.sku);
      setCopied(true);
      toast.success('SKU copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadQR = () => {
    if (!qrData?.qr_code) return;
    
    const link = document.createElement('a');
    link.download = `QR_${qrData.sku}_${productName.replace(/\s+/g, '_')}.png`;
    link.href = qrData.qr_code;
    link.click();
    toast.success('QR descargado');
  };

  const handlePrintLabel = () => {
    if (!qrData) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Etiqueta - ${productName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            display: flex; 
            justify-content: center; 
            align-items: center; 
            min-height: 100vh;
            background: #f5f5f4;
          }
          .label {
            width: 280px;
            background: white;
            border: 2px solid #e7e5e4;
            border-radius: 12px;
            padding: 20px;
            text-align: center;
          }
          .logo { 
            font-size: 14px; 
            font-weight: bold; 
            color: #059669; 
            margin-bottom: 10px;
          }
          .qr { 
            width: 180px; 
            height: 180px; 
            margin: 0 auto 15px;
          }
          .product-name { 
            font-size: 16px; 
            font-weight: bold; 
            color: #292524; 
            margin-bottom: 8px;
            line-height: 1.2;
          }
          .price { 
            font-size: 24px; 
            font-weight: bold; 
            color: #059669; 
            margin-bottom: 10px;
          }
          .sku { 
            font-size: 12px; 
            color: #78716c; 
            font-family: monospace;
            background: #f5f5f4;
            padding: 4px 8px;
            border-radius: 4px;
            display: inline-block;
          }
          .clip-info {
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px dashed #d6d3d1;
            font-size: 10px;
            color: #a8a29e;
          }
          @media print {
            body { background: white; }
            .label { border: 1px solid #ccc; }
          }
        </style>
      </head>
      <body>
        <div class="label">
          <div class="logo">Lunch & Roll</div>
          <img src="${qrData.qr_code}" class="qr" alt="QR Code" />
          <div class="product-name">${productName}</div>
          <div class="price">$${price.toFixed(2)} MXN</div>
          <div class="sku">SKU: ${qrData.sku}</div>
          <div class="clip-info">
            Escanea con tu app o ingresa el SKU en Clip
          </div>
        </div>
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (!showQR) {
    return (
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => setShowQR(true)}
        className="gap-2"
      >
        <QrCode className="w-4 h-4" />
        Ver QR del producto
      </Button>
    );
  }

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-stone-800 flex items-center gap-2">
          <QrCode className="w-5 h-5 text-emerald-600" />
          Código QR del Producto
        </h3>
        <button 
          onClick={() => setShowQR(false)}
          className="text-stone-400 hover:text-stone-600 text-sm"
        >
          Cerrar
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : qrData ? (
        <>
          {/* QR Image */}
          <div className="flex justify-center">
            <div className="bg-white p-4 rounded-xl border border-stone-100 shadow-sm">
              <img 
                src={qrData.qr_code} 
                alt={`QR Code for ${productName}`}
                className="w-48 h-48"
              />
            </div>
          </div>

          {/* Product Info for Vendors */}
          <div className="bg-stone-50 rounded-xl p-4 space-y-3">
            <div className="text-center">
              <p className="text-sm text-stone-500">Para cobro con Clip</p>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600">SKU:</span>
              <div className="flex items-center gap-2">
                <code className="bg-white px-3 py-1 rounded-lg border text-stone-800 font-mono">
                  {qrData.sku}
                </code>
                <button
                  onClick={handleCopySKU}
                  className="p-1.5 rounded-lg hover:bg-stone-200 transition-colors"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-stone-400" />
                  )}
                </button>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600">Precio:</span>
              <span className="font-bold text-emerald-700 text-lg">${price.toFixed(2)} MXN</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleDownloadQR}
              className="flex-1 gap-2"
            >
              <Download className="w-4 h-4" />
              Descargar QR
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={handlePrintLabel}
              className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              <Printer className="w-4 h-4" />
              Imprimir Etiqueta
            </Button>
          </div>
        </>
      ) : (
        <p className="text-center text-stone-500 py-4">
          No se pudo cargar el QR
        </p>
      )}
    </div>
  );
};

export default ProductQRCode;
